<template>
  <div class="audit-page">

    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Auditoria</div>
          <div class="page-subtitle">{{ logs.total || 0 }} eventos registados</div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="content-wrapper">

      <!-- Filters -->
      <div class="filters-card">
        <div class="filters-grid">

          <div class="filter-field">
            <label class="filter-label">Acção</label>
            <q-input
              v-model="filters.action"
              outlined dense clearable
              placeholder="ex: user.delete"
              class="filter-input"
            >
              <template v-slot:prepend><q-icon name="bolt" size="16px" /></template>
            </q-input>
          </div>

          <div class="filter-field">
            <label class="filter-label">Entidade</label>
            <q-select
              v-model="filters.entity_type"
              :options="entityOptions"
              outlined dense emit-value map-options clearable
              placeholder="Todas"
              class="filter-input"
            >
              <template v-slot:prepend><q-icon name="category" size="16px" /></template>
            </q-select>
          </div>

          <div class="filter-field">
            <label class="filter-label">Data de</label>
            <q-input v-model="filters.date_from" type="date" outlined dense class="filter-input">
              <template v-slot:prepend><q-icon name="event" size="16px" /></template>
            </q-input>
          </div>

          <div class="filter-field">
            <label class="filter-label">Data até</label>
            <q-input v-model="filters.date_to" type="date" outlined dense class="filter-input">
              <template v-slot:prepend><q-icon name="event" size="16px" /></template>
            </q-input>
          </div>

          <div class="filter-field">
            <label class="filter-label">Por página</label>
            <q-select
              v-model="filters.per_page"
              :options="[10, 20, 50, 100]"
              outlined dense
              class="filter-input"
            >
              <template v-slot:prepend><q-icon name="format_list_numbered" size="16px" /></template>
            </q-select>
          </div>

        </div>

        <div class="filter-actions">
          <button class="filter-btn filter-btn--clear" @click="clearFilters">
            <q-icon name="clear_all" size="15px" />
            Limpar
          </button>
          <button class="filter-btn filter-btn--apply" @click="applyFilters">
            <q-icon name="search" size="15px" />
            Pesquisar
          </button>
        </div>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="log-list">
        <div v-for="n in 6" :key="n" class="skeleton-entry">
          <q-skeleton type="text" width="18%" class="q-mb-sm" />
          <q-skeleton type="text" width="70%" class="q-mb-xs" />
          <q-skeleton type="text" width="45%" />
        </div>
      </div>

      <!-- Empty state -->
      <div v-else-if="logs.data.length === 0" class="empty-state">
        <div class="empty-icon-wrap">
          <q-icon name="manage_search" size="48px" color="grey-4" />
        </div>
        <div class="empty-title">Nenhum evento encontrado</div>
        <div class="empty-sub">Ajuste os filtros e tente novamente.</div>
      </div>

      <!-- Log entries -->
      <div v-else class="log-list">
        <div
          v-for="log in logs.data"
          :key="log.id"
          class="log-entry"
          :class="`log-entry--${actionCategory(log.action)}`"
        >
          <div class="log-main">
            <div class="log-content">

              <!-- Badges -->
              <div class="log-badges">
                <span class="action-badge" :class="`action-badge--${actionCategory(log.action)}`">
                  {{ log.action }}
                </span>
                <span class="entity-badge" :class="`entity-badge--${(log.entity_type || '').toLowerCase()}`">
                  {{ log.entity_type }}
                </span>
                <span class="entity-id-badge">#{{ log.entity_id }}</span>
              </div>

              <!-- Description -->
              <div class="log-description">{{ log.description }}</div>

              <!-- Meta -->
              <div class="log-meta">
                <span class="meta-item">
                  <q-icon name="person" size="13px" />
                  {{ log.admin?.name }}
                  <span class="meta-dim">· {{ log.admin?.email }}</span>
                </span>
                <span class="meta-sep">·</span>
                <span class="meta-item">
                  <q-icon name="router" size="13px" />
                  {{ log.ip_address }}
                </span>
                <span class="meta-sep">·</span>
                <span class="meta-item">
                  <q-icon name="schedule" size="13px" />
                  {{ formatDate(log.created_at) }}
                </span>
              </div>
            </div>

            <!-- Expand button -->
            <button
              v-if="log.previous_state || log.new_state"
              class="expand-btn"
              :class="{ 'expand-btn--open': expanded === log.id }"
              @click="toggle(log.id)"
              title="Ver alterações"
            >
              <q-icon name="expand_more" size="18px" />
            </button>
          </div>

          <!-- State diff -->
          <div v-if="expanded === log.id" class="state-diff">
            <div class="diff-col" v-if="log.previous_state">
              <div class="diff-label diff-label--before">
                <q-icon name="history" size="13px" /> Antes
              </div>
              <pre class="diff-code diff-code--before">{{ JSON.stringify(log.previous_state, null, 2) }}</pre>
            </div>
            <div class="diff-arrow" v-if="log.previous_state && log.new_state">
              <q-icon name="arrow_forward" size="16px" />
            </div>
            <div class="diff-col" v-if="log.new_state">
              <div class="diff-label diff-label--after">
                <q-icon name="check_circle" size="13px" /> Depois
              </div>
              <pre class="diff-code diff-code--after">{{ JSON.stringify(log.new_state, null, 2) }}</pre>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && logs.last_page > 1" class="pagination-row">
        <q-pagination
          v-model="currentPage"
          :max="logs.last_page"
          :max-pages="6"
          direction-links
          boundary-links
          color="primary"
          active-design="unelevated"
          active-color="primary"
          active-text-color="white"
          @update:model-value="fetchLogs"
        />
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import store from '@/vuex/store';

const loading    = ref(true);
const currentPage = ref(1);
const expanded   = ref(null);

const logs = computed(() => store.state.auditlog.logs);

const filters = ref({
  action: '',
  entity_type: null,
  date_from: '',
  date_to: '',
  per_page: 20,
});

const entityOptions = [
  { label: 'Utilizador',  value: 'User' },
  { label: 'Imóvel',      value: 'Property' },
  { label: 'Carousel',    value: 'Carousel' },
];

function actionCategory(action) {
  if (action?.startsWith('user.'))      return 'user';
  if (action?.startsWith('property.'))  return 'property';
  if (action?.startsWith('carousel.'))  return 'carousel';
  return 'other';
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function toggle(id) {
  expanded.value = expanded.value === id ? null : id;
}

function buildParams() {
  const p = { page: currentPage.value, per_page: filters.value.per_page };
  if (filters.value.action)      p.action      = filters.value.action;
  if (filters.value.entity_type) p.entity_type = filters.value.entity_type;
  if (filters.value.date_from)   p.date_from   = filters.value.date_from;
  if (filters.value.date_to)     p.date_to     = filters.value.date_to;
  return p;
}

async function fetchLogs() {
  loading.value = true;
  try {
    await store.dispatch('auditlog/getLogs', buildParams());
  } finally {
    loading.value = false;
  }
}

function applyFilters() {
  currentPage.value = 1;
  fetchLogs();
}

function clearFilters() {
  filters.value = { action: '', entity_type: null, date_from: '', date_to: '', per_page: 20 };
  currentPage.value = 1;
  fetchLogs();
}

onMounted(fetchLogs);
</script>

<style scoped>
/* ── Page ──────────────────────────────────────────── */
.audit-page {
  background: #f4f6fb;
  min-height: 100vh;
}

/* ── Header ────────────────────────────────────────── */
.page-header {
  background: #0d1b2a;
  padding: 20px 24px;
}
.page-header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
}
.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}
.page-subtitle {
  font-size: 12px;
  color: rgba(212, 175, 55, 0.75);
  letter-spacing: 0.4px;
  margin-top: 2px;
}

/* ── Content ───────────────────────────────────────── */
.content-wrapper {
  max-width: 1200px;
  margin: 28px auto;
  padding: 0 20px 64px;
}

/* ── Filters card ──────────────────────────────────── */
.filters-card {
  background: #fff;
  border: 1px solid #e8ecf4;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
}
.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}
.filter-field { display: flex; flex-direction: column; }
.filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.3px;
  margin-bottom: 5px;
  text-transform: uppercase;
}

:deep(.filter-input .q-field__control) { border-radius: 10px; }
:deep(.filter-input .q-field__control:before) { border-color: #e2e8f0; }
:deep(.filter-input .q-field__control:hover:before) { border-color: #0d1b2a; }
:deep(.filter-input.q-field--focused .q-field__control:before) {
  border-color: #d4af37 !important;
  border-width: 2px;
}
:deep(.filter-input .q-field__prepend) { color: #b0bec5; }
:deep(.filter-input.q-field--focused .q-field__prepend) { color: #0d1b2a; }

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
}
.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.18s;
}
.filter-btn--clear {
  background: #f1f5f9;
  color: #64748b;
}
.filter-btn--clear:hover { background: #e2e8f0; }
.filter-btn--apply {
  background: #0d1b2a;
  color: #d4af37;
}
.filter-btn--apply:hover { background: #162333; }

/* ── Skeleton ──────────────────────────────────────── */
.skeleton-entry {
  background: #fff;
  border: 1px solid #e8ecf4;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 10px;
}

/* ── Empty ─────────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 16px;
  text-align: center;
}
.empty-icon-wrap {
  width: 88px;
  height: 88px;
  background: #fff;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8ecf4;
  box-shadow: 0 4px 16px rgba(13,27,42,.06);
  margin-bottom: 20px;
}
.empty-title { font-size: 18px; font-weight: 700; color: #0f172a; }
.empty-sub { font-size: 13px; color: #94a3b8; margin-top: 6px; }

/* ── Log list ──────────────────────────────────────── */
.log-list { display: flex; flex-direction: column; gap: 10px; }

.log-entry {
  background: #fff;
  border: 1px solid #e8ecf4;
  border-radius: 14px;
  overflow: hidden;
  border-left-width: 4px;
  transition: box-shadow 0.22s;
}
.log-entry:hover { box-shadow: 0 4px 18px rgba(13,27,42,.07); }

.log-entry--user     { border-left-color: #2563eb; }
.log-entry--property { border-left-color: #16a34a; }
.log-entry--carousel { border-left-color: #7c3aed; }
.log-entry--other    { border-left-color: #94a3b8; }

.log-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px;
  gap: 12px;
}
.log-content { flex: 1; min-width: 0; }

/* Badges */
.log-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.action-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.2px;
}
.action-badge--user     { background: rgba(37,99,235,.1);  color: #1d4ed8; }
.action-badge--property { background: rgba(22,163,74,.1);  color: #15803d; }
.action-badge--carousel { background: rgba(124,58,237,.1); color: #6d28d9; }
.action-badge--other    { background: #f1f5f9; color: #64748b; }

.entity-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.entity-badge--user     { background: rgba(37,99,235,.08);  color: #2563eb; }
.entity-badge--property { background: rgba(22,163,74,.08);  color: #16a34a; }
.entity-badge--carousel { background: rgba(124,58,237,.08); color: #7c3aed; }

.entity-id-badge {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}

/* Description */
.log-description {
  font-size: 13.5px;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 10px;
  line-height: 1.5;
}

/* Meta */
.log-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}
.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.meta-item .q-icon { color: #94a3b8; }
.meta-dim { color: #94a3b8; }
.meta-sep { color: #cbd5e1; }

/* Expand button */
.expand-btn {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 8px;
  background: #f1f5f9;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: background 0.18s, transform 0.25s;
}
.expand-btn:hover { background: #e2e8f0; }
.expand-btn--open {
  transform: rotate(180deg);
  background: #e2e8f0;
}

/* ── State diff ────────────────────────────────────── */
.state-diff {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0 20px 16px;
  border-top: 1px solid #f1f5f9;
  padding-top: 14px;
  flex-wrap: wrap;
}
.diff-col { flex: 1; min-width: 140px; }
.diff-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 6px;
  margin-bottom: 8px;
}
.diff-label--before { background: rgba(220,38,38,.08); color: #dc2626; }
.diff-label--after  { background: rgba(22,163,74,.08); color: #16a34a; }

.diff-code {
  background: #f8fafc;
  border: 1px solid #e8ecf4;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 12px;
  font-family: 'Courier New', monospace;
  color: #334155;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}
.diff-code--after {
  background: rgba(22,163,74,.04);
  border-color: rgba(22,163,74,.15);
  color: #166534;
}

.diff-arrow {
  padding-top: 28px;
  color: #94a3b8;
  flex-shrink: 0;
}

/* ── Pagination ────────────────────────────────────── */
.pagination-row {
  display: flex;
  justify-content: center;
  padding-top: 32px;
}
</style>
